'use client'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError } from '@/lib/utils/fetch-api'
import { notifyError } from '@/lib/utils/error-utils'

/** 初回を除く再試行の上限。枯渇している最中に再送を積み増さないため小さく保つ。 */
const MAX_RETRY = 2

const RETRY_BASE_MS = 1_000
const RETRY_CAP_MS = 8_000
/** 抽選が 0 に寄っても即時再送にしない下限。 */
const RETRY_MIN_MS = 250
/** サーバーが異常に長い Retry-After を返しても待ち続けないための上限。 */
const RETRY_AFTER_MAX_MS = 60_000

/**
 * query（読み取り）の再試行方針。
 *
 * 2026-08-08 の本番障害まで、この判定は `isServerError`（status >= 500）を一律で false にしており、
 * 503 / 502 / 504 という「時間をおけば回復する失敗」まで弾いていた。
 * その結果、Supavisor の接続枯渇で 55 種類の API が同時に 503 を返したとき、
 * react-query を使う画面のどこからも自動回復できず、利用者は手動リロードを繰り返すしかなかった。
 *
 * 判定は次の順で行う。順序自体が仕様であり、`query-provider.test.ts` で固定している。
 *   1. 上限に達していたら打ち切る
 *   2. 認証切れは再試行しない（再送しても回復しないため。status が 503 でも code が
 *      AUTH_* ならこちらが先に効く）
 *   3. 一過性の失敗（408/429/502/503/504・ネットワーク層）は再試行する
 *   4. それ以外の ApiError（500 のバグ・4xx・呼び出し側による中断）は再試行しない
 *   5. ApiError でない例外は従来どおり上限まで再試行する
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRY) return false

  if (error instanceof ApiError) {
    if (error.isAuthError) return false
    if (error.isTransient) return true
    return false
  }

  return true
}

/**
 * mutation（書き込み）の再試行方針。
 *
 * **一過性の失敗であっても再送しない。** query 側の一過性リトライをここへ波及させてはならない。
 * このリポジトリには汎用の冪等キー機構が無く（`Idempotency-Key` 相当のヘッダ処理は存在しない）、
 * `useMutation` の実装は POST / PATCH / PUT / DELETE が大半を占める。
 * サーバーが処理を継続している最中にクライアントだけがタイムアウトして再送すると、
 * 契約・入金・顧客データの二重登録になる。
 *
 * なお、ここで `status === 0`（TIMEOUT / NETWORK_ERROR / OFFLINE）も false にしたのは
 * 従来挙動からの意図的な後退である。従来はネットワーク瞬断の書き込みが自動再送で救われていたが、
 * 見えないまま二重登録が起きるより、失敗を利用者に見せて明示的に再実行してもらう方を選ぶ。
 */
export function shouldRetryMutation(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRY) return false

  if (error instanceof ApiError) return false

  return true
}

/**
 * 再試行までの待機時間。
 *
 * サーバーが Retry-After で明示指示していればそれに従い、無ければ full jitter で散らす。
 * jitter が無いと、同時に被災した多数のクライアントが同じ瞬間に再送して山を作り、
 * 復旧しかけた接続プールを再び潰す。
 */
export function retryDelay(failureCount: number, error: unknown): number {
  if (
    error instanceof ApiError &&
    typeof error.retryAfterMs === 'number' &&
    error.retryAfterMs > 0
  ) {
    return Math.min(error.retryAfterMs, RETRY_AFTER_MAX_MS)
  }

  const ceiling = Math.min(RETRY_BASE_MS * 2 ** failureCount, RETRY_CAP_MS)
  return Math.max(RETRY_MIN_MS, Math.random() * ceiling)
}

export function QueryProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 30 * 1000,
            gcTime: process.env.NODE_ENV === 'development' ? 60 * 1000 : 5 * 60 * 1000,
            retry: shouldRetryQuery,
            retryDelay,
            refetchOnWindowFocus: false,
          },
          mutations: {
            // 読み取りとは別の方針を使う。ここを shouldRetryQuery と共有してはならない。
            retry: shouldRetryMutation,
            retryDelay,
            onError: (error) => {
              notifyError(error)
            },
          },
        },
      })
  )

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
}
